import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Tooltip from '../glass/Tooltip';
import type { LucideIcon } from 'lucide-react';

export interface LayerMenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  onSelect: () => void;
  disabled?: boolean;
  /** Shown as a native tooltip; use it to explain WHY an item is disabled. */
  tooltip?: string;
  /** Renders a divider above this item. */
  separatorBefore?: boolean;
  danger?: boolean;
}

/**
 * Contextual layer menu.
 *
 * Purely presentational — it renders whatever items the caller supplies, which
 * the caller derives from `getLayerCapabilities()`. That keeps a single menu
 * working across every service type.
 *
 * Behaviour: closes on outside click and Escape, flips above / shifts left when
 * near a viewport edge, and supports full keyboard navigation.
 */
export default function LayerOptionsMenu({
  items,
  anchorRef,
  point,
  onClose,
  ariaLabel = 'Layer options',
}: {
  items: LayerMenuItem[];
  /** The usual trigger: an overflow button's own rect, flush against its
   *  bottom-right corner. Omit when using `point` instead. */
  anchorRef?: React.RefObject<HTMLElement>;
  /** The right-click alternative: open flush against a raw viewport
   *  coordinate (the pointer event's clientX/clientY) rather than an
   *  element's rect — there is no "anchor button" for a context-menu
   *  trigger. Exactly one of `anchorRef`/`point` is expected. */
  point?: { x: number; y: number };
  onClose: () => void;
  /** This menu started life for the layer 3-dot button; other callers (the
   *  user menu, say) need their own label rather than inheriting that one. */
  ariaLabel?: string;
}) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const enabledIndexes = items
    .map((item, index) => (item.disabled ? -1 : index))
    .filter((index) => index >= 0);

  // Position after mount so the real menu size is known (needed to flip/shift).
  useLayoutEffect(() => {
    const menu = menuRef.current;
    if (!menu) return;
    const anchor = anchorRef?.current;
    if (!anchor && !point) return;

    // The box the menu opens against: an anchor element's own rect, or a 0×0
    // box at the click point — either way "bottom/right of this box" below
    // reads the same.
    const anchorRect: { top: number; bottom: number; left: number; right: number } = anchor
      ? anchor.getBoundingClientRect()
      : { top: point!.y, bottom: point!.y, left: point!.x, right: point!.x };
    const menuRect = menu.getBoundingClientRect();
    const margin = 8;

    let top = anchorRect.bottom + 4;
    let left = anchorRect.right - menuRect.width;

    // Flip above the anchor when there is not enough room below.
    if (top + menuRect.height > window.innerHeight - margin) {
      const above = anchorRect.top - menuRect.height - 4;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - menuRect.height - margin);
    }
    // Keep it inside the horizontal bounds.
    left = Math.min(Math.max(margin, left), window.innerWidth - menuRect.width - margin);

    setPosition({ top, left });
  }, [anchorRef, point, items.length]);

  useEffect(() => {
    menuRef.current?.focus();
  }, []);

  // Outside click / Escape / scroll-away all dismiss.
  useEffect(() => {
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target) || anchorRef?.current?.contains(target)) return;
      onClose();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onClose);
    };
  }, [anchorRef, onClose]);

  const move = useCallback(
    (direction: 1 | -1) => {
      if (enabledIndexes.length === 0) return;
      setActiveIndex((current) => {
        const position = enabledIndexes.indexOf(current);
        const next = position === -1 ? 0 : (position + direction + enabledIndexes.length) % enabledIndexes.length;
        return enabledIndexes[next];
      });
    },
    [enabledIndexes],
  );

  /**
   * Portalled to <body>.
   *
   * The menu is positioned in VIEWPORT coordinates (measured from the anchor),
   * but the TOC it is triggered from is a glass surface — and a
   * `backdrop-filter` ancestor becomes the containing block for `position:
   * fixed`, which would offset the menu by the TOC's origin. Rendering into
   * <body> keeps `fixed` meaning "relative to the viewport".
   */
  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      tabIndex={-1}
      aria-label={ariaLabel}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          move(1);
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          move(-1);
        } else if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          const item = items[activeIndex];
          if (item && !item.disabled) {
            item.onSelect();
            onClose();
          }
        } else if (event.key === 'Home') {
          event.preventDefault();
          setActiveIndex(enabledIndexes[0] ?? 0);
        } else if (event.key === 'End') {
          event.preventDefault();
          setActiveIndex(enabledIndexes[enabledIndexes.length - 1] ?? 0);
        }
      }}
      // Glass contextual menu; geometry/animation come from the design system.
      className="gl-menu fixed z-[6000] w-56 overflow-hidden py-1 focus:outline-none"
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        // Avoid a flash at the wrong spot before measurement completes.
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const isActive = index === activeIndex;
        return (
          <React.Fragment key={item.id}>
            {item.separatorBefore && <div className="gl-divider my-1" />}
            <Tooltip content={item.tooltip ?? ''} placement="left" delay={500}>
            <button
              type="button"
              role="menuitem"
              disabled={item.disabled}
              // Drives the hover/active tint from the design system rather than
              // a hard-coded background class.
              data-active={isActive && !item.disabled}
              onMouseEnter={() => !item.disabled && setActiveIndex(index)}
              onClick={() => {
                if (item.disabled) return;
                item.onSelect();
                onClose();
              }}
              className={`gl-menu-item ${item.danger ? 'gl-menu-item--danger' : ''} flex w-full items-center gap-2.5 px-3 py-2 text-left text-[13px]
                ${item.disabled ? 'cursor-not-allowed opacity-40' : ''}`}
              style={{ color: item.danger ? 'var(--bv-danger)' : 'var(--glass-text)' }}
            >
              <Icon className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </button>
            </Tooltip>
          </React.Fragment>
        );
      })}
    </div>,
    document.body,
  );
}
